/**
 * Disables page scrolling, typically used when opening modal windows.
 * - Saves the scrollbar width in the custom property `--scrollbar-width` to prevent content from shifting.
 * - Sets the `overflow: hidden` style on body to block scrolling.
 * - Adds the `modal-open` class to apply the appropriate styles.
 */

export function disableScroll() {
  const docEl = document.documentElement;
  const htmlDataset = /** @type {any} */ (docEl.dataset);
  if (!('prevOverflow' in htmlDataset)) {
    htmlDataset.prevOverflow = docEl.style.overflow || '';
  }
  docEl.style.overflow = 'hidden';
  docEl.classList.add('modal-open-root');
  // Prefer locking inner scroll containers (centerbar/profile) to avoid page-level layout shifts
  const containers = Array.from(document.querySelectorAll('.posts-scroll-container, .profile-scroll-container, .centerbar-posts'));
  if (containers.length > 0) {
    containers.forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      if (!('prevOverflow' in el.dataset)) {
        el.dataset.prevOverflow = el.style.overflow || '';
      }
      el.style.overflow = 'hidden';
      // Only mark scrollbar-hidden as added if it wasn't present originally
      if (!el.classList.contains('scrollbar-hidden')) {
        el.classList.add('scrollbar-hidden');
        el.dataset.scrollbarHiddenAdded = '1';
      }
    });
    document.body.classList.add('modal-open');
    document.body.style.overflow = 'hidden';
    return; // Do not touch body when inner containers handle scroll
  }

  const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
  // Only apply right padding if the page actually has a vertical scrollbar
  if (scrollBarWidth > 0) {
    document.body.style.setProperty('--scrollbar-width', `${scrollBarWidth}px`);
  } else {
    // Ensure no stale value sticks around when there was no scrollbar
    document.body.style.removeProperty('--scrollbar-width');
  }
  document.body.style.overflow = 'hidden';
  document.body.classList.add('modal-open');
}

/**
 * Enables page scrolling, typically used when closing modal windows.
 * - Removes the `modal-open` class from body, restoring standard scrolling behavior.
 * - Removes the custom property `--scrollbar-width` that was set when disabling scrolling.
 * - Restores the `overflow: auto` style, allowing page scrolling.
 */
export function enableScroll() {
  const docEl = document.documentElement;
  const htmlDataset = /** @type {any} */ (docEl.dataset);
  // First restore any inner scroll containers we may have locked
  const containers = Array.from(document.querySelectorAll('.posts-scroll-container, .profile-scroll-container, .centerbar-posts'));
  if (containers.length > 0) {
    containers.forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const prev = /** @type {any} */(el.dataset).prevOverflow ?? '';
      el.style.overflow = prev;
      delete /** @type {any} */(el.dataset).prevOverflow;
      // Only remove scrollbar-hidden if we added it during disableScroll
      if (/** @type {any} */(el.dataset).scrollbarHiddenAdded === '1') {
        el.classList.remove('scrollbar-hidden');
        delete /** @type {any} */(el.dataset).scrollbarHiddenAdded;
      }
    });
  }

  document.body.classList.remove('modal-open');
  document.body.style.removeProperty('--scrollbar-width');
  // Clear any implicit right padding added by CSS using the variable
  document.body.style.paddingRight = '';
  document.body.style.overflow = 'auto';
  const prevHtmlOverflow = htmlDataset.prevOverflow ?? '';
  docEl.style.overflow = prevHtmlOverflow;
  delete htmlDataset.prevOverflow;
  docEl.classList.remove('modal-open-root');
}

/**
 * Toggles the scrolling state depending on whether a modal window is open.
 * @param {boolean} isModalOpen - Flag indicating whether the modal window is open.
 * If `isModalOpen` is true, scrolling is disabled; if false, scrolling is enabled.
 */
export const toggleScroll = isModalOpen => (isModalOpen ? disableScroll() : enableScroll());

/**
 * Scrolls the page to the top.
 * This can be useful, for example, when navigating to another page or when opening a modal window.
 */
export const scrollToTop = () => {
  window.scrollTo(0, 0);
};

/**
 * Scrolls the page to the element with the specified id.
 * @param {string} elementId - ID of the element to scroll to.
 * @param {number} delay - Delay before scrolling in milliseconds.
 */
export const scrollToElement = (elementId, delay = 300) => {
  const savedPosition = localStorage.getItem('scrollTo');
  if (savedPosition === elementId) {
    setTimeout(() => {
      window.scrollTo({
        top: Number(document.getElementById(elementId)?.offsetTop ?? 0),
        behavior: 'smooth',
      });
    }, delay);
    localStorage.removeItem('scrollTo');
  }
};

/**
 * Adds a scroll event handler.
 * @param {Function} onScrollCallback - Callback function for handling the scroll event.
 */
export const addScrollListener = (onScrollCallback) => {
  /** @type {(this: Window, ev: Event) => any} */
  const handler = (ev) => onScrollCallback(ev);
  // @ts-ignore store original for removal if needed
  onScrollCallback.__handler = handler;
  window.addEventListener('scroll', handler);
};

/**
 * Removes a scroll event handler.
 * @param {Function} onScrollCallback - Callback function to remove the handler.
 */
export const removeScrollListener = (onScrollCallback) => {
  // use stored handler if available
  // @ts-ignore
  const handler = onScrollCallback.__handler || onScrollCallback;
  window.removeEventListener('scroll', handler);
};

/**
 * Saves the current scroll position and the top visible post for the current route
 * @param {string} routeName - Route name (e.g., 'home')
 */
export const saveScrollPosition = routeName => {
  const container = document.querySelector('.posts-scroll-container');
  const scrollPosition = container ? container.scrollTop : window.scrollY;
  const scrollState = JSON.parse(localStorage.getItem('scrollState') || '{}');

  const visiblePostId = findVisiblePostId();

  scrollState[routeName] = {
    position: scrollPosition,
    postId: visiblePostId,
    timestamp: Date.now(),
  };

  localStorage.setItem('scrollState', JSON.stringify(scrollState));
};

/**
 * Restores the saved scroll position for the current route
 * @param {string} routeName - Route name (e.g., 'home')
 * @param {number} delay - Delay in ms before restoring the position
 */
export const restoreScrollPosition = (routeName, delay = 300) => {
  setTimeout(() => {
    const scrollState = JSON.parse(localStorage.getItem('scrollState') || '{}');
    const savedState = scrollState[routeName];

    if (savedState) {
      const container = document.querySelector('.posts-scroll-container');
      const scrollTo = (/** @type {number} */ top) => {
        if (container) {
          container.scrollTop = top;
        } else {
          window.scrollTo({ top, behavior: 'auto' });
        }
      };

      // Use raw scrollTop position as the primary restore method — it's the value
      // saved directly from container.scrollTop so it's already in container coordinates.
      // postId is kept only as a fallback for when the position is 0 (user was at top).
      if (savedState.position) {
        scrollTo(savedState.position);
      } else if (savedState.postId && container) {
        const postElement = document.getElementById(savedState.postId);
        if (postElement) {
          // getBoundingClientRect gives position relative to the viewport; adjust by
          // the container's own rect and its current scrollTop to get container-relative offset.
          const postTop = postElement.getBoundingClientRect().top
            - container.getBoundingClientRect().top
            + container.scrollTop;
          container.scrollTop = postTop;
        }
      }

      // If more than 30 minutes have passed, delete the saved state
      const thirtyMinutesInMs = 30 * 60 * 1000;
      if (Date.now() - savedState.timestamp > thirtyMinutesInMs) {
        delete scrollState[routeName];
        localStorage.setItem('scrollState', JSON.stringify(scrollState));
      }
    }
  }, delay);
};

/**
 * Finds the ID of the top visible post in the feed
 * @returns {string|null} ID of the top visible post or null
 */
const findVisiblePostId = () => {
  const container = document.querySelector('.posts-scroll-container');
  const containerRect = container
    ? container.getBoundingClientRect()
    : { top: 0, bottom: window.innerHeight };
  const posts = Array.from(document.querySelectorAll('.post'));
  for (const post of posts) {
    const rect = post.getBoundingClientRect();
    if (rect.top >= containerRect.top && rect.top < containerRect.bottom) {
      return post.id || null;
    }
  }
  return null;
}

